import { useState, useEffect, useRef } from 'react';
import { Navigation } from '../../components/Navigation';
import { useAuthStore } from '../../store/authStore';
import { resumeTemplateService, ResumeTemplate } from '../../lib/resumeTemplateService';
import { ResumeTemplateRenderer } from '../../components/ResumeTemplateRenderer';
import { FileText, Heart, Search, Filter, Star, Check, X, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const sampleResumeData = {
  personal: {
    fullName: 'Alex Johnson',
    email: 'alex.johnson@email.com',
    phone: '(555) 123-4567',
    location: 'Detroit, MI',
  },
  summary: 'Results-driven professional with 8+ years of experience specializing in project management, team leadership, and business development. Proven track record of delivering high-impact results and driving organizational success through innovative problem-solving and collaborative teamwork.',
  experience: [
    {
      company: 'TechCorp Solutions',
      title: 'Senior Project Manager',
      startDate: '2020',
      endDate: 'Present',
      bullets: [
        'Led cross-functional teams of 15+ members to deliver 20+ projects on time and under budget',
        'Increased team productivity by 40% through implementation of agile methodologies',
        'Managed $2M+ annual project portfolio with 98% client satisfaction rate',
      ],
    },
    {
      company: 'Innovation Labs',
      title: 'Project Coordinator',
      startDate: '2017',
      endDate: '2020',
      bullets: [
        'Coordinated 10+ concurrent projects across multiple departments',
        'Reduced project delivery time by 25% through process optimization',
      ],
    },
  ],
  education: [
    {
      school: 'University of Michigan',
      degree: "Bachelor's",
      field: 'Business Administration',
      graduationDate: '2016',
    },
  ],
  skills: ['Project Management', 'Agile/Scrum', 'Leadership', 'Stakeholder Management', 'Risk Assessment', 'Budget Planning', 'Team Building', 'Strategic Planning'],
  certifications: ['PMP Certified', 'Certified Scrum Master', 'Six Sigma Green Belt'],
};

export function TemplateGallery() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<ResumeTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<ResumeTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [sortBy, setSortBy] = useState<'popularity' | 'ats' | 'newest'>('popularity');
  const [favoriteTemplateIds, setFavoriteTemplateIds] = useState<Set<string>>(new Set());
  const [selectedTemplate, setSelectedTemplate] = useState<ResumeTemplate | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  // TODO: Add PDF preview generation using previewRefs
  const _previewRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (user) {
      loadTemplates();
      loadFavorites();
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [templates, searchQuery, selectedCategory, selectedIndustries, showFavoritesOnly, sortBy]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await resumeTemplateService.getTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    if (!user) return;
    const favorites = await resumeTemplateService.getFavoriteTemplates(user.id);
    setFavoriteTemplateIds(new Set(favorites.map(t => t.id)));
  };

  const applyFilters = () => {
    let filtered = [...templates];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        t =>
          t.name.toLowerCase().includes(query) ||
          t.description.toLowerCase().includes(query)
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }

    if (selectedIndustries.length > 0) {
      filtered = filtered.filter(t =>
        selectedIndustries.some(industry => t.industry_tags.includes(industry))
      );
    }

    if (showFavoritesOnly) {
      filtered = filtered.filter(t => favoriteTemplateIds.has(t.id));
    }

    filtered.sort((a, b) => {
      if (sortBy === 'popularity') {
        return b.popularity_score - a.popularity_score;
      } else if (sortBy === 'ats') {
        return b.ats_score - a.ats_score;
      } else {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

    setFilteredTemplates(filtered);
  };

  const handleToggleFavorite = async (templateId: string) => {
    if (!user) return;

    const success = await resumeTemplateService.toggleFavoriteTemplate(user.id, templateId);
    if (success) {
      await loadFavorites();
    }
  };

  const handleSelectTemplate = (template: ResumeTemplate) => {
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  const handleUseTemplate = (templateId: string) => {
    navigate(`/jobseeker/resume-builder?templateId=${templateId}`);
  };

  const handleToggleIndustry = (industry: string) => {
    setSelectedIndustries(prev =>
      prev.includes(industry)
        ? prev.filter(i => i !== industry)
        : [...prev, industry]
    );
  };

  const allIndustries = Array.from(
    new Set(templates.flatMap(t => t.industry_tags))
  ).sort();

  const allCategories = Array.from(
    new Set(templates.map(t => t.category))
  ).sort();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Resume Templates</h1>
            <p className="text-xl text-gray-600">
              Choose from {templates.length} professional, ATS-optimized templates
            </p>
          </div>

          <div className="mb-6 flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search templates..."
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-cyan-500 focus:outline-none"
              />
            </div>

            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className="px-6 py-3 bg-white border-2 border-gray-300 rounded-xl font-semibold hover:border-cyan-500 transition flex items-center justify-center gap-2"
            >
              <Filter className="w-5 h-5" />
              Filters
              {(selectedCategory !== 'all' || selectedIndustries.length > 0) && (
                <span className="bg-cyan-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {(selectedCategory !== 'all' ? 1 : 0) + selectedIndustries.length}
                </span>
              )}
            </button>

            <button
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              className={`px-6 py-3 rounded-xl font-semibold transition flex items-center gap-2 ${
                showFavoritesOnly
                  ? 'bg-red-500 text-white'
                  : 'bg-white border-2 border-gray-300 hover:border-red-500'
              }`}
            >
              <Heart className={`w-5 h-5 ${showFavoritesOnly ? 'fill-current' : ''}`} />
              Favorites
            </button>

            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as any)}
              className="px-4 py-3 bg-white border-2 border-gray-300 rounded-xl font-semibold hover:border-cyan-500 focus:border-cyan-500 focus:outline-none cursor-pointer"
            >
              <option value="popularity">Most Popular</option>
              <option value="ats">Highest ATS Score</option>
              <option value="newest">Newest</option>
            </select>
          </div>

          {showFilterPanel && (
            <div className="mb-6 p-6 bg-white rounded-2xl border-2 border-gray-200 shadow-lg">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-bold text-gray-900 mb-3">Category</h3>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="category"
                        checked={selectedCategory === 'all'}
                        onChange={() => setSelectedCategory('all')}
                        className="w-4 h-4 text-cyan-500"
                      />
                      <span>All Categories</span>
                    </label>
                    {allCategories.map(category => (
                      <label key={category} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="category"
                          checked={selectedCategory === category}
                          onChange={() => setSelectedCategory(category)}
                          className="w-4 h-4 text-cyan-500"
                        />
                        <span className="capitalize">{category}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-3">Industry</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {allIndustries.map(industry => (
                      <label key={industry} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedIndustries.includes(industry)}
                          onChange={() => handleToggleIndustry(industry)}
                          className="w-4 h-4 text-cyan-500 rounded"
                        />
                        <span>{industry}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setSelectedCategory('all');
                    setSelectedIndustries([]);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
                >
                  Clear Filters
                </button>
                <button
                  onClick={() => setShowFilterPanel(false)}
                  className="px-4 py-2 bg-cyan-500 text-white rounded-lg font-semibold hover:bg-cyan-600 transition"
                >
                  Apply
                </button>
              </div>
            </div>
          )}

          {filteredTemplates.length === 0 ? (
            <div className="text-center py-20">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No templates found</h2>
              <p className="text-gray-600 mb-6">Try adjusting your filters or search query</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                  setSelectedIndustries([]);
                  setShowFavoritesOnly(false);
                }}
                className="px-6 py-3 bg-cyan-500 text-white rounded-lg font-semibold hover:bg-cyan-600 transition"
              >
                Reset All Filters
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTemplates.map(template => {
                const isFavorite = favoriteTemplateIds.has(template.id);

                return (
                  <div
                    key={template.id}
                    className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 hover:border-cyan-300 hover:shadow-xl transition-all overflow-hidden group"
                  >
                    <div
                      className="relative aspect-[8.5/11] bg-gray-50 cursor-pointer overflow-hidden"
                      onClick={() => handleSelectTemplate(template)}
                    >
                      <div
                        className="absolute inset-0 flex items-center justify-center"
                        style={{ transform: 'scale(0.45)' }}
                      >
                        <ResumeTemplateRenderer
                          template={template}
                          resumeData={sampleResumeData}
                          scale={1}
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-6">
                        <button className="px-6 py-3 bg-white text-cyan-600 rounded-xl font-bold shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-transform">
                          View Details
                        </button>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-1">
                            {template.name}
                          </h3>
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {template.description}
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFavorite(template.id);
                          }}
                          className={`p-2 rounded-lg transition ${
                            isFavorite
                              ? 'bg-red-100 text-red-600'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                        >
                          <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                        </button>
                      </div>

                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-lg text-sm font-semibold">
                          <Check className="w-4 h-4" />
                          {template.ats_score}% ATS
                        </div>
                        <div className="flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-800 rounded-lg text-sm font-semibold">
                          <Star className="w-4 h-4 fill-current" />
                          {template.popularity_score}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {template.features.slice(0, 3).map((feature, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-cyan-50 text-cyan-700 text-xs font-medium rounded"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>

                      <button
                        onClick={() => handleUseTemplate(template.id)}
                        className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-bold hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg flex items-center justify-center gap-2"
                      >
                        <Sparkles className="w-5 h-5" />
                        Use This Template
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showPreviewModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">{selectedTemplate.name}</h2>
                <p className="text-gray-600 mt-1">{selectedTemplate.description}</p>
              </div>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setSelectedTemplate(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            <div className="p-6">
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="p-4 bg-green-50 rounded-xl">
                  <div className="text-3xl font-bold text-green-800 mb-1">
                    {selectedTemplate.ats_score}%
                  </div>
                  <div className="text-sm text-green-700 font-semibold">ATS Score</div>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl">
                  <div className="text-3xl font-bold text-amber-800 mb-1">
                    {selectedTemplate.popularity_score}
                  </div>
                  <div className="text-sm text-amber-700 font-semibold">Popularity</div>
                </div>
                <div className="p-4 bg-cyan-50 rounded-xl">
                  <div className="text-3xl font-bold text-cyan-800 mb-1 capitalize">
                    {selectedTemplate.layout_type.replace('_', ' ')}
                  </div>
                  <div className="text-sm text-cyan-700 font-semibold">Layout</div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-bold text-gray-900 mb-3">Features</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedTemplate.features.map((feature, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg font-medium"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-bold text-gray-900 mb-3">Best For</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedTemplate.industry_tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-bold text-gray-900 mb-3">Preview</h3>
                <div className="border-2 border-gray-200 rounded-xl overflow-hidden bg-gray-50 flex items-center justify-center" style={{ height: '600px' }}>
                  <div style={{ transform: 'scale(0.55)', transformOrigin: 'center' }}>
                    <ResumeTemplateRenderer
                      template={selectedTemplate}
                      resumeData={sampleResumeData}
                      scale={1}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => handleToggleFavorite(selectedTemplate.id)}
                  className={`flex-1 py-3 rounded-xl font-bold transition flex items-center justify-center gap-2 ${
                    favoriteTemplateIds.has(selectedTemplate.id)
                      ? 'bg-red-500 text-white hover:bg-red-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Heart
                    className={`w-5 h-5 ${
                      favoriteTemplateIds.has(selectedTemplate.id) ? 'fill-current' : ''
                    }`}
                  />
                  {favoriteTemplateIds.has(selectedTemplate.id) ? 'Favorited' : 'Add to Favorites'}
                </button>
                <button
                  onClick={() => handleUseTemplate(selectedTemplate.id)}
                  className="flex-1 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-bold hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-5 h-5" />
                  Use This Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
